import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import useFetch from 'use-http';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';
import { Badge } from '@/components/ui/badge';

export default function PrescriptionTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);
  const [loading, setLoading] = useState(true);

  const { get: getPrescriptions, response: prescriptionsResponse } = useFetch(
    '/UserAccount/PrescriptionList',
  );
  const { get: getPrescription } = useFetch('/UserAccount/Prescription');

  const fetchData = async (date = dateRange, searchTerm = search) => {
    setLoading(true);

    sessionDispatch({
      type: 'UPDATE',
      data: { dateFilter: date },
    });

    const query = getQueryString(
      date,
      searchTerm,
      undefined,
      undefined,
      userState.espbiEsi,
    );
    const result = await getPrescriptions(query);

    if (prescriptionsResponse.ok) {
      const sorted = _.orderBy(result, ['dateAsserted'], ['desc']);
      const mapped = sorted.map((item) => ({
        id: item.id,
        title: item.name,
        date: item.dateAsserted,
        valid: isNaN(new Date(item.effectiveEndDate).getTime())
          ? undefined
          : new Date(item.effectiveEndDate) > new Date(),
      }));
      setData(mapped);
    }

    setLoading(false);
  };

  const handleSelection = async (id) => {
    setLoading(true);
    const query = userState.espbiEsi
      ? `?id=${id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${id}`;
    const res = await getPrescription(query);
    history.push('/userAccount/prescriptions/details', { data: res });
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, userState?.espbiEsi]);

  return (
    <DataTable
      title="Receptai"
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={['title', 'date']}
      loading={loading}
      onRowClick={(row) => handleSelection(row.id)}
    />
  );
}
const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const columns = ({ handleSelection }) => [
  {
    accessorKey: 'title',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="RECEPTAS" />
    ),
    cell: ({ row }) => (
      <span className="font-medium max-w-[500px] truncate ">
        {capitalizeFirstLetter(row.original.title)}
      </span>
    ),
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const rawDate = row.original.date;
      const dateObj = new Date(rawDate);
      const isMidnight =
        dateObj.getUTCHours() === 0 && dateObj.getUTCMinutes() === 0;
      return (
        <span className="text-muted-foreground">
          {rawDate
            ? format(dateObj, isMidnight ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm')
            : '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'valid',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="GALIOJIMAS" />
    ),
    cell: ({ row }) => {
      const valid = row.original.valid;
      const variant =
        valid === true ? 'success' : valid === false ? 'fail' : 'neutral';
      const label =
        valid === true
          ? 'Galioja'
          : valid === false
          ? 'Negalioja'
          : 'Nežinomas';

      return <Badge variant={variant}>{label}</Badge>;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex gap-2 justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original.id);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
